/**
 * Shared inputs for both estimate modes: house type, built-up area, storeys, finishing level
 */

import React from 'react';
import { Box, Chip, Grid, Stack, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { FieldLabel, NumberField, OptionCard } from '../ui';
import HouseIllustration from './HouseIllustration';
import { COST_RATES, HOUSE_TYPES } from '../../utils/costRates';
import { formatNumber } from '../../utils/format';

export const MIN_AREA = 300;

const AREA_PRESETS = {
  kampung: [700, 900, 1200],
  terrace: [1000, 1400, 2200],
  bungalow: [1800, 2500, 3500],
};

export const isBasicsValid = (draft) =>
  !!draft &&
  !!HOUSE_TYPES[draft.houseType] &&
  Number(draft.builtUpArea) >= MIN_AREA &&
  [1, 2, 3].includes(Number(draft.floors)) &&
  !!COST_RATES[draft.finishingLevel];

const HouseBasicsFields = ({ draft, onChange, t, language, showErrors = false, showProjectName = true }) => {
  const my = language === 'my';
  const areaError = showErrors && !(Number(draft.builtUpArea) >= MIN_AREA);
  const presets = AREA_PRESETS[draft.houseType] || AREA_PRESETS.terrace;

  return (
    <Stack spacing={3}>
      <Box>
        <FieldLabel>{t.houseType}</FieldLabel>
        <Grid container spacing={1.5} role="radiogroup" aria-label={t.houseType}>
          {Object.entries(HOUSE_TYPES).map(([id, type]) => (
            <Grid item xs={4} key={id}>
              <OptionCard
                selected={draft.houseType === id}
                onClick={() => onChange({ houseType: id })}
                media={
                  <Box sx={{ height: { xs: 40, sm: 52 } }}>
                    <HouseIllustration type={id} />
                  </Box>
                }
                title={my ? type.labelMy : type.label}
                description={
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {my ? type.descriptionMy : type.description}
                  </Box>
                }
                sx={{ p: { xs: 1.25, sm: 1.75 } }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box>
        <NumberField
          label={t.builtUpArea}
          value={draft.builtUpArea}
          onChange={(v) => onChange({ builtUpArea: v })}
          unit={t.sqft}
          integer
          placeholder={t.areaPlaceholder}
          error={areaError}
          helperText={areaError ? t.areaTooSmall : t.builtUpAreaHint}
        />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
          <Box component="span" sx={{ typography: 'caption', color: 'text.secondary', mr: 0.5 }}>
            {t.areaPresets}:
          </Box>
          {presets.map((p) => (
            <Chip
              key={p}
              size="small"
              variant={Number(draft.builtUpArea) === p ? 'filled' : 'outlined'}
              color={Number(draft.builtUpArea) === p ? 'primary' : 'default'}
              label={`${formatNumber(p)} ${t.sqft}`}
              onClick={() => onChange({ builtUpArea: String(p) })}
            />
          ))}
        </Stack>
      </Box>

      <Box>
        <FieldLabel>{t.floors}</FieldLabel>
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={Number(draft.floors)}
          onChange={(e, v) => v && onChange({ floors: v })}
          aria-label={t.floors}
        >
          {[1, 2, 3].map((n) => (
            <ToggleButton key={n} value={n} sx={{ py: 1.25 }}>
              {n} {n === 1 ? t.storey : t.storeys}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box>
        <FieldLabel>{t.finishing}</FieldLabel>
        <Grid container spacing={1.5} role="radiogroup" aria-label={t.finishing}>
          {Object.entries(COST_RATES).map(([id, level]) => (
            <Grid item xs={12} sm={4} key={id}>
              <OptionCard
                selected={draft.finishingLevel === id}
                onClick={() => onChange({ finishingLevel: id })}
                badge={level.recommended ? t.recommended : undefined}
                title={my ? level.labelMy : level.label}
                description={(my ? level.featuresMy : level.features).slice(0, 2).join(' · ')}
                meta={`RM ${level.min}–${level.max} ${t.perSqft}`}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {showProjectName && (
        <TextField
          fullWidth
          label={t.projectName}
          placeholder={t.projectNamePlaceholder}
          value={draft.projectName || ''}
          onChange={(e) => onChange({ projectName: e.target.value.slice(0, 60) })}
        />
      )}
    </Stack>
  );
};

export default HouseBasicsFields;
